import { useState, useEffect } from 'react'
import { authApi, LoginHistoryEntry } from '@/services/api'
import { Clock, MapPin, Monitor, User, Globe, CheckCircle, XCircle } from 'lucide-react'
import clsx from 'clsx'

export default function LoginHistoryPage() {
  const [history, setHistory] = useState<LoginHistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [total, setTotal] = useState(0)
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)

  useEffect(() => {
    loadHistory()
  }, [offset])

  const loadHistory = async () => {
    setLoading(true)
    setError(null)

    try {
      const result = await authApi.getLoginHistory(limit, offset)

      if (result.success && result.data) {
        setHistory(result.data)
        setTotal(result.meta?.total || 0)
      } else {
        setError(result.error?.message || '접속이력을 불러올 수 없습니다')
      }
    } catch (err) {
      setError('서버 오류가 발생했습니다')
      console.error('Failed to load login history:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }

  const getProviderLabel = (provider: string) => {
    switch (provider) {
      case 'google': return 'Google'
      case 'kakao': return 'Kakao'
      default: return provider
    }
  }

  const parseUserAgent = (ua: string) => {
    // Simple user agent parsing
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua)
    const browser = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/)?.[0] || 'Unknown'
    const os = ua.match(/(Windows|Mac OS X|Linux|Android|iOS)[\s\d._]*/)?.[0] || 'Unknown'

    return { isMobile, browser, os }
  }

  return (
    <div className="min-h-screen bg-bg-primary p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-2">접속 이력</h1>
          <p className="text-text-secondary">
            전체 사용자의 로그인 기록을 확인할 수 있습니다
            {total > 0 && ` (총 ${total.toLocaleString()}건)`}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-gold"></div>
          </div>
        )}

        {/* History List */}
        {!loading && history.length > 0 && (
          <div className="space-y-3">
            {history.map((entry) => {
              const { isMobile, browser, os } = parseUserAgent(entry.user_agent)

              return (
                <div
                  key={entry.id}
                  className={clsx(
                    'bg-bg-secondary rounded-lg p-4 border transition-colors',
                    entry.success ? 'border-border-subtle hover:border-border-default' : 'border-red-500/30 bg-red-500/5'
                  )}
                >
                  <div className="flex items-start gap-4">
                    {/* User Avatar */}
                    <div className="flex-shrink-0">
                      {entry.user_avatar ? (
                        <img
                          src={entry.user_avatar}
                          alt={entry.user_name || entry.email}
                          className="w-10 h-10 rounded-full"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-accent-purple flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                      )}
                    </div>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <h3 className="font-medium text-text-primary truncate">
                            {entry.user_name || entry.email}
                          </h3>
                          <p className="text-sm text-text-tertiary truncate">{entry.email}</p>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {entry.success ? (
                            <span className="flex items-center gap-1 text-xs text-green-500">
                              <CheckCircle className="w-4 h-4" />
                              성공
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-red-500">
                              <XCircle className="w-4 h-4" />
                              실패
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                        {/* Time */}
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Clock className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{formatDate(entry.created_at)}</span>
                        </div>

                        {/* Location */}
                        {(entry.country || entry.city) && (
                          <div className="flex items-center gap-2 text-text-secondary">
                            <MapPin className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">
                              {[entry.city, entry.country].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}

                        {/* IP */}
                        <div className="flex items-center gap-2 text-text-secondary">
                          <Globe className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate font-mono text-xs">{entry.ip_address}</span>
                        </div>

                        {/* Provider */}
                        <div className="flex items-center gap-2 text-text-secondary">
                          <User className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{getProviderLabel(entry.provider)}</span>
                        </div>

                        {/* Device */}
                        <div className="flex items-center gap-2 text-text-secondary col-span-full">
                          <Monitor className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate text-xs">
                            {isMobile ? '📱 Mobile' : '💻 Desktop'} • {browser} • {os}
                          </span>
                        </div>
                      </div>

                      {/* Error Message */}
                      {!entry.success && entry.error_message && (
                        <div className="mt-3 p-2 bg-red-500/10 rounded text-xs text-red-500">
                          {entry.error_message}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && history.length === 0 && !error && (
          <div className="text-center py-12">
            <User className="w-12 h-12 mx-auto mb-4 text-text-tertiary opacity-50" />
            <p className="text-text-secondary">접속 이력이 없습니다</p>
          </div>
        )}

        {/* Pagination */}
        {!loading && total > limit && (
          <div className="mt-6 flex items-center justify-between">
            <button
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={offset === 0}
              className="btn btn-secondary disabled:opacity-50"
            >
              이전
            </button>

            <span className="text-sm text-text-secondary">
              {offset + 1} - {Math.min(offset + limit, total)} / {total.toLocaleString()}
            </span>

            <button
              onClick={() => setOffset(offset + limit)}
              disabled={offset + limit >= total}
              className="btn btn-secondary disabled:opacity-50"
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
